import { Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'

interface SubscriptionEvent {
  id: number
  event_type: string
  organization_name: string
  organization_id: number | null
  plan_name: string | null
  created_at: string
  details: string | null
  price_usd?: number | null
  billing_interval?: string | null
  from_plan?: string | null
  to_plan?: string | null
  from_price_usd?: number | null
  to_price_usd?: number | null
  from_billing_interval?: string | null
  to_billing_interval?: string | null
  status_transition?: string | null
  ltv?: number | null
}

interface SubscriptionEventsFeedProps {
  events: SubscriptionEvent[]
}

const eventVariants: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  new_subscription: 'success',
  cancellation: 'destructive',
  payment_failed: 'warning',
  payment_recovered: 'success',
  upgrade: 'info',
  downgrade: 'secondary',
  reactivation: 'success',
  status_change: 'secondary',
  update: 'secondary',
  deleted: 'destructive',
  unknown: 'secondary',
}

export function SubscriptionEventsFeed({ events }: SubscriptionEventsFeedProps) {
  const { t } = useTranslation('admin')

  if (events.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        {t('dashboard.subscriptionActivity.empty')}
      </p>
    )
  }

  const formatPrice = (price: number | null | undefined, interval: string | null | undefined) => {
    if (!price) return null
    const suffix = interval === 'year' ? t('dashboard.subscriptionActivity.perYear') : t('dashboard.subscriptionActivity.perMonth')
    return `$${price}${suffix}`
  }

  const getEventLabel = (eventType: string) => {
    return t(`dashboard.subscriptionActivity.events.${eventType}`, { defaultValue: t('dashboard.subscriptionActivity.events.unknown') })
  }

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 60) {
      return diffMins <= 1 ? t('dashboard.subscriptionActivity.time.justNow') : t('dashboard.subscriptionActivity.time.minutesAgo', { count: diffMins })
    } else if (diffHours < 24) {
      return t('dashboard.subscriptionActivity.time.hoursAgo', { count: diffHours })
    } else if (diffDays < 7) {
      return t('dashboard.subscriptionActivity.time.daysAgo', { count: diffDays })
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    }
  }

  return (
    <div className="space-y-3">
      {events.map((event) => {
        const variant = eventVariants[event.event_type] || eventVariants.unknown

        return (
          <div
            key={event.id}
            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border"
          >
            <div className="flex items-center gap-3">
              <Badge variant={variant} className="min-w-[90px] justify-center">
                {getEventLabel(event.event_type)}
              </Badge>
              <div>
                {event.organization_id ? (
                  <Link
                    href={`/admin/organizations/${event.organization_id}`}
                    className="text-sm font-medium hover:underline"
                  >
                    {event.organization_name}
                  </Link>
                ) : (
                  <span className="text-sm font-medium">{event.organization_name}</span>
                )}
                {(event.event_type === 'upgrade' || event.event_type === 'downgrade') && event.from_plan && event.to_plan ? (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-xs text-muted-foreground">{event.from_plan}</span>
                    <span className="text-xs text-muted-foreground">→</span>
                    <span className="text-xs font-medium text-foreground">{event.to_plan}</span>
                    {event.from_price_usd && event.to_price_usd && (
                      <span className="text-xs text-muted-foreground ml-1">
                        ({formatPrice(event.from_price_usd, event.from_billing_interval)} → {formatPrice(event.to_price_usd, event.to_billing_interval)})
                      </span>
                    )}
                  </div>
                ) : event.status_transition ? (
                  <p className="text-xs text-muted-foreground">{event.status_transition}</p>
                ) : event.details ? (
                  <p className="text-xs text-muted-foreground">{event.details}</p>
                ) : null}
                {event.ltv != null && event.ltv > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">LTV: ${event.ltv.toFixed(2)}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1.5 justify-end mb-0.5">
                {event.plan_name && (
                  <span className="text-xs text-muted-foreground">{event.plan_name}</span>
                )}
                {event.event_type !== 'upgrade' && event.event_type !== 'downgrade' && event.price_usd ? (
                  <span className="text-xs font-medium text-foreground">
                    {formatPrice(event.price_usd, event.billing_interval)}
                  </span>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatRelativeTime(event.created_at)}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
